import moment from 'moment-timezone';
import { humanize } from 'underscore.string';
import { orderBy } from 'lodash';
import React from 'react';
import _ from 'underscore';
import formatMoney from 'utils/formatters/Money';
import { Translation, useTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import ReferenceNumberTypes from 'constants/ReferenceNumberTypes';
import { OPENTRACK_TRACKING_STATUS } from 'constants/Opentrack';
/* To find and format the data used to populate the set list of simplied events, we see which events are included by order and shipment type, sort by sequence order, discount those where show === false, look at the shipment, order, and stops to find an event timestamp (or audits if not possible), and check the audits for a corresponding update to an event, or at least a record of what user triggered the event.
If there are more than one audit event that points to a given shipment event, we sort the list by most recent.

Audit events pertaining to a stop have also been updated to contain a stopId, allowing us to pair them back to their corresponding shipment stop in a future update. Currently as of 10/22/21, we do not have stopIds on audit events.
*/
const STOP_EVENTS = [
  'picked_up_shipment',
  'delivered',
  'stop_window_change',
  'stop_window_confirmed',
];

const TERMINAL_HOLD_EVENTS = [
  'TERMINAL_HOLD_STATUS_EVENT_ADDED',
  'TERMINAL_HOLD_STATUS_EVENT_REMOVED',
];

const RAIL_EVENT_TYPES = [
  'rail_loaded_changed',
  'rail_departed_changed',
  'rail_arrived_at_facility_changed',
  'rail_unloaded_changed',
  'rail_available_at_facility_changed',
  'rail_outgated_from_facility_changed',
  'rail_lfd_at_facility_changed',
  'rail_eta_final_facility_changed',
  'rail_ingated_at_origin_facility_changed',
  'rail_loaded_at_origin_changed',
  'rail_departed_from_origin_changed',
  'rail_arrived_at_destination_port_facility_changed',
  'rail_unloaded_at_destination_port_changed',
];

const BRIDGE_CODE_EVENTS = [
  'BRIDGE_CODE_EVENT_CREATE',
  'BRIDGE_CODE_EVENT_DELETE',
  'BRIDGE_CODE_EVENT_UPDATE',
];

export const LOADED_ON_RAIL = 'LOADED_ON_RAIL';
export const DEPARTED_ON_RAIL = 'DEPARTED_ON_RAIL';
export const ARRIVED_AT_RAIL_FACILITY = 'ARRIVED_AT_RAIL_FACILITY';
export const UNLOADED_FROM_RAIL = 'UNLOADED_FROM_RAIL';
export const AVAILABLE_AT_RAIL_FACILITY = 'AVAILABLE_AT_RAIL_FACILITY';
export const FULLY_OUTGATED_FROM_RAIL_FACILITY =
  'FULL_OUTGATED_FROM_RAIL_FACILITY';
export const LAST_FREE_DAY_AT_RAIL_FACILITY = 'LAST_FREE_DAY_AT_RAIL_FACILITY';
export const ETA_TO_FINAL_RAIL_FACILITY = 'ETA_TO_FINAL_RAIL_FACILITY';
export const FULL_INGATED_AT_ORIGIN_RAIL_FACILITY =
  'FULL_INGATED_AT_ORIGIN_RAIL_FACILITY';
export const LOADED_AT_RAIL_ORIGIN = 'LOADED_AT_RAIL_ORIGIN';
export const DEPARTED_FROM_ORIGIN_RAIL = 'DEPARTED_FROM_ORIGIN_RAIL';
export const ARRIVED_AT_DESTINATION_RAIL_FACILITY =
  'ARRIVED_AT_DESTINATION_RAIL_FACILITY';
export const UNLOADED_AT_DESTINATION_PORT = 'UNLOADED_AT_DESTINATION_PORT';

export const RAIL_EARLIEST_RETURN_DATE = 'RAIL_EARLIEST_RETURN_DATE';
export const RAIL_CUT_DATE = 'RAIL_CUT_DATE';

export const RAIL_EVENT_LABELS = new Set([
  LOADED_ON_RAIL,
  DEPARTED_ON_RAIL,
  ARRIVED_AT_RAIL_FACILITY,
  UNLOADED_FROM_RAIL,
  AVAILABLE_AT_RAIL_FACILITY,
  FULLY_OUTGATED_FROM_RAIL_FACILITY,
  LAST_FREE_DAY_AT_RAIL_FACILITY,
  ETA_TO_FINAL_RAIL_FACILITY,
  FULL_INGATED_AT_ORIGIN_RAIL_FACILITY,
  LOADED_AT_RAIL_ORIGIN,
  DEPARTED_FROM_ORIGIN_RAIL,
  ARRIVED_AT_DESTINATION_RAIL_FACILITY,
  UNLOADED_AT_DESTINATION_PORT,
  RAIL_EARLIEST_RETURN_DATE,
  RAIL_CUT_DATE,
]);

const populateAndFormatSimplifiedEvents = (
  type,
  shipment,
  stops = [],
  audits = [],
) => {
  let validEvents = eventTimeline.filter(
    (event) => (event.type_of_job || []).includes(type) && event.show,
  );
  validEvents.sort((a, b) => a.sequence - b.sequence);
  let rowData = [];
  const stopsArray = stops ?? [];
  const auditsArray = audits ?? [];
  validEvents.forEach((event) => {
    if (stopsArray && stopsArray.length && event && event.label) {
      let row = event.valueGetter(
        shipment,
        event.label,
        stopsArray,
        auditsArray,
      );
      if (row) {
        row.label = event.label;
        row.event = humanize(event.label);
        rowData.push(row);
      }
    } else {
      // if we don't have the fully populated stop data, we fall back on the stop data attached to a shipment, which lacks timeline metadata.
      let row = event.valueGetter(shipment, shipment.stops ?? [], auditsArray);
      if (row && event && event.label) {
        row.label = event.label;
        row.event = humanize(event.label);
        rowData.push(row);
      }
    }
  });

  const loadedOnRail = rowData.find((row) => row.label === LOADED_ON_RAIL);
  const isLoadedOnRail = !!loadedOnRail?.date;

  const fullIngatedAtOrigin = rowData.find(
    (row) => row.label === FULL_INGATED_AT_ORIGIN_RAIL_FACILITY,
  );
  const isFullIngatedAtOrigin = !!fullIngatedAtOrigin?.date;

  if (
    (type === 'import' && isLoadedOnRail) ||
    (type === 'export' && isFullIngatedAtOrigin) ||
    ['reload', 'intermodal'].includes(type)
  ) {
    return rowData;
  }

  return rowData.filter((row) => !RAIL_EVENT_LABELS.has(row.label));
};

/* Audit events - take all audits and format for row data */

const formatAuditEvents = (formatDateTime, audits = []) => {
  let rowData = [];
  (audits ?? []).forEach((audit) => {
    let row = {
      label: formatAuditLabelForAllView(audit),
      date: formatAuditDateForAllView(audit, formatDateTime),
      reported_date: formatAuditDateForAllView(
        audit,
        formatDateTime,
        'reported_date',
      ),
      source: audit.user.email ? audit.user.email : audit.user.name || null,
      channel: audit.user?.request_source || '',
      event: renameDocumentUploadedEvent(audit.event, audit.meta?.value), // added property for typeahead search - label can be a div.
      meta: audit.meta,
    };
    rowData.push(row);
  });
  return rowData;
};

/* Methods for fetching row data on the simplified view */
const eventValueGetters = {
  getCreatedDate: (shipment, label, shipmentStops = [], audits = []) => {
    let createdEvents = (audits ?? []).filter(
      (audit) => audit.event === 'created',
    );
    if (!createdEvents || !createdEvents.length) {
      return null;
    }
    const createdAudit = getEarliest(createdEvents);
    return {
      date: formatDate(createdAudit.timestamp),
      reported_date: formatDate(
        createdAudit?.meta?.date
          ? createdAudit.meta.date
          : createdAudit.timestamp,
      ),
      updated: formatDate(getLatest(createdEvents).timestamp),
      source: getEmailOfShipmentCreator(audits),
      channel: createdAudit?.user?.request_source || '',
    };
  },
  getTrackingInitiatedDate: (
    shipment,
    label,
    shipmentStops = [],
    audits = [],
  ) => {
    let trackingInitiatedEvents = (audits ?? []).filter(
      (audit) => audit?.event === OPENTRACK_TRACKING_STATUS.TRACKING_INITIATED,
    );

    const trackingAudit = getEarliest(trackingInitiatedEvents);
    return formatSimplfiedRowData(
      trackingAudit?.timestamp,
      trackingAudit,
      null,
      true,
      formatDate(getLatest(trackingInitiatedEvents)?.timestamp),
    );
  },
  getEarliestDateToGetEmpty: (
    shipment,
    label,
    shipmentStops = [],
    audits = [],
  ) => {
    const eventAudit = searchAuditsForEvent(
      'earliest_empty_date_changed',
      audits,
      null,
    );
    return formatSimplfiedRowData(
      shipment.earliest_empty_date,
      eventAudit,
      null,
      label,
    );
  },
  getEmptyPickup: (shipment, label, shipmentStops = [], audits = []) => {
    let stops = (shipmentStops ?? []).filter(
      (stop) =>
        stop.action === 'pickup' &&
        stop.empty === true &&
        stop.complete === true &&
        stop.timeline.complete,
    );
    let date = searchCompletedStopForActualDateOfEvent(stops, 'complete');
    let stopId = getStopId(stops);
    let stopAudit = searchAuditsForEvent('picked_up_shipment', audits, stopId);
    return formatSimplfiedRowData(date, stopAudit, null, label);
  },
  getEstimatedArrivalDate: (
    shipment,
    label,
    shipmentStops = [],
    audits = [],
  ) => {
    let eventAudit = searchAuditsForEvent(
      'estimated_arrival_changed',
      audits,
      null,
    );
    return formatSimplfiedRowData(
      shipment.estimated_arrival,
      eventAudit,
      null,
      label,
    );
  },
  getActualTimeOfArrival: (
    shipment,
    label,
    shipmentStops = [],
    audits = [],
  ) => {
    let eventAudit = searchAuditsForEvent('arrival_date_changed', audits, null);
    return formatSimplfiedRowData(
      shipment.arrival_date,
      eventAudit,
      null,
      label,
    );
  },
  getDischarged: (shipment, label, shipmentStops = [], audits = []) => {
    let eventAudit = searchAuditsForEvent(
      'discharge_date_changed',
      audits,
      null,
    );
    return formatSimplfiedRowData(
      shipment.discharge_date,
      eventAudit,
      null,
      label,
    );
  },
  getCustomLfd: (shipment, label, shipmentStops = [], audits = []) => {
    let eventAudit = searchAuditsForEvent('custom_lfd_changed', audits, null);
    return formatSimplfiedRowData(shipment.custom_lfd, eventAudit, null, label);
  },
  getOnHold: (shipment, label, shipmentStops = [], _audits = []) => {
    const shipmentHolds = shipment.holds ?? [];

    if (!shipmentHolds.length) {
      return {
        date: null,
        reported_date: null,
        updated: null,
        source: null,
        channel: null,
      };
    }

    const unresolvedHolds = shipmentHolds.filter((hold) => {
      return !hold.resolved_at;
    });

    if (!unresolvedHolds.length) {
      return {
        date: null,
        reported_date: null,
        updated: null,
        source: null,
        channel: null,
      };
    }

    const lastUnresolved = _.last(unresolvedHolds);
    const lastUnresolvedDate = moment(lastUnresolved.reported_at).format('L');
    return {
      date: lastUnresolvedDate,
      reported_date: lastUnresolved?.meta?.date
        ? moment(lastUnresolved.meta.date).format('L')
        : lastUnresolvedDate,
      updated: lastUnresolvedDate,
      source: lastUnresolved.source,
      channel: lastUnresolved?.user?.request_source || '',
    };
  },
  getAvailable: (shipment, label, shipmentStops = [], audits = []) => {
    let eventAudit = searchAuditsForEvent(
      'available_date_changed',
      audits,
      null,
    );
    return formatSimplfiedRowData(
      shipment.available_date,
      eventAudit,
      null,
      label,
    );
  },
  getOffsitePickupScheduled: (
    shipment,
    label,
    shipmentStops = [],
    audits = [],
  ) => {
    let stops = (shipmentStops ?? []).filter((stop) =>
      getPickupStopValue(stop),
    );
    let result = rowInformation(stops, audits, 'stop_window_change');
    const isDrayage = shipment?.shipment_type === 'drayage';
    return formatSimplfiedRowData(
      result.date,
      result.eventAudit,
      result.userEmail,
      isDrayage,
    );
  },
  getPickupAppointmentConfirmed: (
    shipment,
    label,
    shipmentStops = [],
    audits = [],
  ) => {
    let stops = (shipmentStops ?? []).filter((stop) =>
      getPickupStopValue(stop),
    );
    let result = rowInformation(stops, audits, 'stop_window_change');
    const isDrayage = shipment?.shipment_type === 'drayage';
    return formatSimplfiedRowData(
      result.date,
      result.eventAudit,
      result.userEmail,
      isDrayage,
    );
  },
  getOffsitePickup: (shipment, label, shipmentStops = [], audits = []) => {
    let stops = (shipmentStops ?? []).filter(
      (stop) =>
        getPickupStopValue(stop) && stop.complete && stop.timeline.complete,
    );
    let stopId = getStopId(stops);
    let eventAudit = searchAuditsForEvent('picked_up_shipment', audits, stopId);
    let date = searchCompletedStopForActualDateOfEvent(stops, 'complete');
    return formatSimplfiedRowData(date, eventAudit, null, label);
  },
  getOffistePickupAppointmentConfirmed: (
    shipment,
    label,
    shipmentStops = [],
    audits = [],
  ) => {
    let stops = (shipmentStops ?? []).filter(
      (stop) =>
        getPickupStopValue(stop) && stop.complete && stop.timeline.complete,
    );
    let stopId = getStopId(stops);
    let eventAudit = searchAuditsForEvent('picked_up_shipment', audits, stopId);
    let date = searchCompletedStopForActualDateOfEvent(stops, 'complete');
    return formatSimplfiedRowData(date, eventAudit, null, label);
  },
  getFullOutgate: (shipment, label, shipmentStops = [], audits = []) => {
    let eventAudit = searchAuditsForEvent(
      'outgated_date_changed',
      audits,
      null,
    );
    return formatSimplfiedRowData(
      shipment.outgated_date,
      eventAudit,
      null,
      label,
    );
  },
  getPickupScheduled: (shipment, label, shipmentStops = [], audits = []) => {
    let stops = (shipmentStops ?? []).filter(
      (stop) =>
        stop.action === 'pickup' &&
        stop.off_site === false &&
        stop.canceled === false,
    );
    let result = rowInformation(stops, audits, 'stop_window_change');
    const isDrayage = shipment?.shipment_type === 'drayage';
    return formatSimplfiedRowData(
      result.date,
      result.eventAudit,
      result.userEmail,
      isDrayage,
    );
  },
  getArrivedAtPickup: (shipment, label, shipmentStops = [], audits = []) => {
    let stops = (shipmentStops ?? []).filter(
      (stop) =>
        stop.action === 'pickup' &&
        stop.off_site === false &&
        stop.canceled === false &&
        stop.timeline.arrived,
    );
    let stopId = getStopId(stops);
    let eventAudit = searchAuditsForEvent('arrived_at_origin', audits, stopId);
    let date = searchCompletedStopForActualDateOfEvent(stops, 'arrived');
    return formatSimplfiedRowData(date, eventAudit, null, label);
  },
  getPickup: (shipment, label, shipmentStops = [], audits = []) => {
    let stops = (shipmentStops ?? []).filter((stop) => {
      return (
        stop.action === 'pickup' &&
        stop.complete &&
        stop.empty === false &&
        stop.canceled === false &&
        stop.timeline.complete
      );
    });
    let stopId = getStopId(stops);
    let eventAudit = searchAuditsForEvent('picked_up_shipment', audits, stopId);
    let date = searchCompletedStopForActualDateOfEvent(stops, 'complete');
    return formatSimplfiedRowData(date, eventAudit, null, label);
  },
  getLFD: (shipment, label, shipmentStops = [], audits = []) => {
    let eventAudit = searchAuditsForEvent(
      'last_free_date_changed',
      audits,
      null,
    );
    return formatSimplfiedRowData(
      shipment.last_free_date,
      eventAudit,
      null,
      label,
    );
  },
  getYardLFD: (shipment, label, shipmentStops = [], audits = []) => {
    let eventAudit = searchAuditsForEvent(
      'yard_last_free_date_changed',
      audits,
      null,
    );
    return formatSimplfiedRowData(
      shipment.yard_last_free_date,
      eventAudit,
      null,
      label,
    );
  },
  getEarliestReturnDate: (shipment, label, shipmentStops = [], audits = []) => {
    const eventAudit = searchAuditsForEvent(
      'earliest_return_date_changed',
      audits,
      null,
    );
    return formatSimplfiedRowData(
      shipment.earliest_return_date,
      eventAudit,
      null,
      label,
    );
  },
  getOffsiteDeliveryScheduled: (
    shipment,
    label,
    shipmentStops = [],
    audits = [],
  ) => {
    let stops = (shipmentStops ?? []).filter(
      (stop) =>
        stop.action === 'deliver' &&
        stop.off_site === true &&
        stop.canceled === false,
    );
    let result = rowInformation(stops, audits, 'stop_window_change');
    const isDrayage = shipment?.shipment_type === 'drayage';
    return formatSimplfiedRowData(
      result.date,
      result.eventAudit,
      result.userEmail,
      isDrayage,
    );
  },
  getOffsiteDeliveryAppointmentConfirmed: (
    shipment,
    label,
    shipmentStops = [],
    audits = [],
  ) => {
    let stops = (shipmentStops ?? []).filter((stop) =>
      getDeliveryStopAppointmentConfirmedValue(stop),
    );
    let result = rowInformation(stops, audits, 'stop_window_change');
    const isDrayage = shipment?.shipment_type === 'drayage';
    return formatSimplfiedRowData(
      result.date,
      result.eventAudit,
      result.userEmail,
      isDrayage,
    );
  },
  getOffsiteDelivery: (shipment, label, shipmentStops = [], audits = []) => {
    let stops = (shipmentStops ?? []).filter(
      (stop) =>
        stop.action === 'deliver' &&
        stop.off_site === true &&
        stop.canceled === false &&
        stop.complete &&
        stop.timeline.complete,
    );
    let stopId = getStopId(stops);
    let eventAudit = searchAuditsForEvent('delivered', audits, stopId);
    let date = searchCompletedStopForActualDateOfEvent(stops, 'complete');
    return formatSimplfiedRowData(date, eventAudit, null, label);
  },
  getDeliveryScheduled: (shipment, label, shipmentStops = [], audits = []) => {
    const isDrayage = shipment?.shipment_type === 'drayage';
    let stops = (shipmentStops ?? []).filter((stop) =>
      getDeliveryStopValue(stop),
    );
    let result = rowInformation(stops, audits, 'stop_window_change');
    return formatSimplfiedRowData(
      result.date,
      result.eventAudit,
      result.userEmail,
      isDrayage,
    );
  },
  getDeliveryAppointmentConfirmed: (
    shipment,
    label,
    shipmentStops = [],
    audits = [],
  ) => {
    let stops = (shipmentStops ?? []).filter((stop) =>
      getDeliveryStopAppointmentConfirmedValue(stop),
    );
    let result = rowInformation(stops, audits, 'stop_window_change');
    const isDrayage = shipment?.shipment_type === 'drayage';
    return formatSimplfiedRowData(
      result.date,
      result.eventAudit,
      result.userEmail,
      isDrayage,
    );
  },
  getArrivedAtDelivery: (shipment, label, shipmentStops = [], audits = []) => {
    let stops = (shipmentStops ?? []).filter(
      (stop) => getDeliveryStopValue(stop) && stop.timeline.arrived,
    );
    let stopId = getStopId(stops);
    let date = searchCompletedStopForActualDateOfEvent(stops, 'arrived');
    let eventAudit = searchAuditsForEvent(
      'arrived_at_destination',
      audits,
      stopId,
    );
    return formatSimplfiedRowData(date, eventAudit, null, label);
  },
  getDelivery: (shipment, label, shipmentStops = [], audits = []) => {
    let stops = (shipmentStops ?? []).filter((stop) => {
      return (
        stop.action === 'deliver' &&
        stop.complete &&
        stop.empty === false &&
        stop.canceled === false &&
        stop.timeline.complete
      );
    });
    let stopId = getStopId(stops);
    let eventAudit = searchAuditsForEvent('delivered', audits, stopId);
    let date = searchCompletedStopForActualDateOfEvent(stops, 'complete');
    return formatSimplfiedRowData(date, eventAudit, null, label);
  },
  getLastCutDate: (shipment, label, shipmentStops = [], audits = []) => {
    // TBD - lacking on shipment, only on order
  },
  getPODUploadDate: (shipment, label, shipmentStops = [], audits = []) => {
    let date;
    let updated;
    let source;
    let channel;
    const orderType = shipment.order && shipment.order.order_type;
    const lastDeliveryStop = (shipmentStops ?? [])
      .filter(
        (stop) =>
          stop.action === 'deliver' &&
          (orderType === 'reposition' || !stop.empty),
      )
      .pop();
    if (lastDeliveryStop) {
      let lastDeliveryStopPODEvents = (audits ?? []).filter((audit) => {
        return (
          (audit.event === 'proof_of_delivery_uploaded' ||
            audit.event === 'proof_of_delivery_removed') &&
          audit.meta.stopId &&
          audit.meta.stopId.toString() === lastDeliveryStop._id.toString()
        );
      });

      const latestEvent = getLatest(
        orderBy(lastDeliveryStopPODEvents, ['timestamp'], ['desc']),
      );
      if (latestEvent?.event === 'proof_of_delivery_uploaded') {
        date = formatDate(latestEvent.timestamp);
        updated = date;
        source = latestEvent.user && latestEvent.user.email;
        channel = latestEvent?.user?.request_source || '';
      }
    }
    return {
      date,
      updated,
      source,
      channel,
    };
  },
  getEmptyReadyToPickup: (shipment, label, shipmentStops = [], audits = []) => {
    return formatSimplfiedRowData(
      shipment.empty_ready_for_pickup,
      null,
      null,
      label,
    );
  },
  getEmptyReturnReported: (
    shipment,
    label,
    shipmentStops = [],
    audits = [],
  ) => {
    return formatSimplfiedRowData(
      shipment.empty_returned_date,
      null,
      null,
      label,
    );
  },

  getEmptyReturn: (shipment, label, shipmentStops = [], audits = []) => {
    const stops = (shipmentStops ?? []).filter((stop) => {
      return (
        stop.action === 'deliver' &&
        !stop.off_site &&
        !stop.canceled &&
        stop.complete &&
        stop.timeline.complete &&
        stop.empty
      );
    });
    const eventAudit = searchAuditsForEvent('delivered', audits, null);
    const date = searchCompletedStopForActualDateOfEvent(stops, 'complete');

    return formatSimplfiedRowData(date, eventAudit, null, label);
  },
  getInvoiceCreated: (shipment, label, shipmentStops = [], audits = []) => {
    let eventAudit = searchAuditsForEvent('invoice_created', audits, null);
    if (!eventAudit) {
      eventAudit = searchAuditsForEvent(
        'shipment_invoice_created',
        audits,
        null,
      );
    }
    const updatedEventAudit = searchAuditsForEvent(
      'invoice_updated',
      audits,
      null,
    );
    const date = eventAudit ? eventAudit.timestamp : null;
    const updated = updatedEventAudit ? updatedEventAudit.timestamp : null;
    return formatSimplfiedRowData(date, eventAudit, null, label, updated);
  },
  getCreditMemoCreated: (shipment, label, shipmentStops = [], audits = []) => {
    let eventAudit = searchAuditsForEvent('credit_memo_created', audits, null);
    if (!eventAudit) {
      eventAudit = searchAuditsForEvent(
        'shipment_credit_memo_created',
        audits,
        null,
      );
    }
    const date = eventAudit ? eventAudit.timestamp : null;
    return formatSimplfiedRowData(date, eventAudit, null, label, null);
  },
  getPayoutCreated: (shipment, label, shipmentStops = [], audits = []) => {
    const eventAudit = searchAuditsForEvent('payout_created', audits, null);
    const updatedEventAudit = searchAuditsForEvent(
      'payout_updated',
      audits,
      null,
    );
    const date = eventAudit ? eventAudit.timestamp : null;
    const updated = updatedEventAudit ? updatedEventAudit.timestamp : null;
    return formatSimplfiedRowData(date, eventAudit, null, label, updated);
  },
  getRailLoaded: (shipment, label, shipmentStops = [], audits = []) => {
    let eventAudit = searchAuditsForEvent('rail_loaded_changed', audits, null);
    return formatSimplfiedRowData(
      shipment.rail_loaded,
      eventAudit,
      null,
      label,
    );
  },
  getRailDeparted: (shipment, label, shipmentStops = [], audits = []) => {
    let eventAudit = searchAuditsForEvent(
      'rail_departed_changed',
      audits,
      null,
    );
    return formatSimplfiedRowData(
      shipment.rail_departed,
      eventAudit,
      null,
      label,
    );
  },
  getRailArrivedAtFacility: (
    shipment,
    label,
    shipmentStops = [],
    audits = [],
  ) => {
    let eventAudit = searchAuditsForEvent(
      'rail_arrived_at_facility_changed',
      audits,
      null,
    );
    return formatSimplfiedRowData(
      shipment.rail_arrived_at_facility,
      eventAudit,
      null,
      label,
    );
  },
  getRailUnloaded: (shipment, label, shipmentStops = [], audits = []) => {
    let eventAudit = searchAuditsForEvent(
      'rail_unloaded_changed',
      audits,
      null,
    );
    return formatSimplfiedRowData(
      shipment.rail_unloaded,
      eventAudit,
      null,
      label,
    );
  },
  getRailAvailableAtFacility: (
    shipment,
    label,
    shipmentStops = [],
    audits = [],
  ) => {
    let eventAudit = searchAuditsForEvent(
      'rail_available_at_facility_changed',
      audits,
      null,
    );
    return formatSimplfiedRowData(
      shipment.rail_available_at_facility,
      eventAudit,
      null,
      label,
    );
  },
  getRailOutgatedAtFacility: (
    shipment,
    label,
    shipmentStops = [],
    audits = [],
  ) => {
    let eventAudit = searchAuditsForEvent(
      'rail_outgated_from_facility_changed',
      audits,
      null,
    );
    return formatSimplfiedRowData(
      shipment.rail_outgated_from_facility,
      eventAudit,
      null,
      label,
    );
  },
  getRailLfdAtFacility: (shipment, label, shipmentStops = [], audits = []) => {
    let eventAudit = searchAuditsForEvent(
      'rail_lfd_at_facility_changed',
      audits,
      null,
    );
    return formatSimplfiedRowData(
      shipment.rail_lfd_at_facility,
      eventAudit,
      null,
      label,
    );
  },
  getRailEtaFinalFacility: (
    shipment,
    label,
    shipmentStops = [],
    audits = [],
  ) => {
    let eventAudit = searchAuditsForEvent(
      'rail_eta_final_facility_changed',
      audits,
      null,
    );
    return formatSimplfiedRowData(
      shipment.rail_eta_final_facility,
      eventAudit,
      null,
      label,
    );
  },
  getRailIngatedAtOriginFacility: (
    shipment,
    label,
    shipmentStops = [],
    audits = [],
  ) => {
    let eventAudit = searchAuditsForEvent(
      'rail_ingated_at_origin_facility_changed',
      audits,
      null,
    );
    return formatSimplfiedRowData(
      shipment.rail_ingated_at_origin_facility,
      eventAudit,
      null,
      label,
    );
  },
  getRailLoadedAtOrigin: (shipment, label, shipmentStops = [], audits = []) => {
    let eventAudit = searchAuditsForEvent(
      'rail_loaded_at_origin_changed',
      audits,
      null,
    );
    return formatSimplfiedRowData(
      shipment.rail_loaded_at_origin,
      eventAudit,
      null,
      label,
    );
  },
  getRailDepartedFromOrigin: (
    shipment,
    label,
    shipmentStops = [],
    audits = [],
  ) => {
    let eventAudit = searchAuditsForEvent(
      'rail_departed_from_origin_changed',
      audits,
      null,
    );
    return formatSimplfiedRowData(
      shipment.rail_departed_from_origin,
      eventAudit,
      null,
      label,
    );
  },
  getRailArrivedAtDestinationPortFacility: (
    shipment,
    label,
    shipmentStops = [],
    audits = [],
  ) => {
    let eventAudit = searchAuditsForEvent(
      'rail_arrived_at_destination_port_facility_changed',
      audits,
      null,
    );
    return formatSimplfiedRowData(
      shipment.rail_arrived_at_destination_port_facility,
      eventAudit,
      null,
      label,
    );
  },
  getRailUnloadedAtDestinationPort: (
    shipment,
    label,
    shipmentStops = [],
    audits = [],
  ) => {
    let eventAudit = searchAuditsForEvent(
      'rail_unloaded_at_destination_port_changed',
      audits,
      null,
    );
    return formatSimplfiedRowData(
      shipment.rail_unloaded_at_destination_port,
      eventAudit,
      null,
      label,
    );
  },
  getTrackingFailedDate: (shipment, label, shipmentStops = [], audits = []) => {
    let trackingFailedEvents = (audits ?? []).filter(
      (audit) => audit?.event === OPENTRACK_TRACKING_STATUS.TRACKING_FAILED,
    );

    const trackingAudit = getEarliest(trackingFailedEvents);
    return formatSimplfiedRowData(
      trackingAudit?.timestamp,
      trackingAudit,
      null,
      true,
      formatDate(getLatest(trackingFailedEvents)?.timestamp),
    );
  },
  getTrackingCompletedDate: (
    shipment,
    label,
    shipmentStops = [],
    audits = [],
  ) => {
    let trackingCompletedEvents = (audits ?? []).filter(
      (audit) => audit?.event === OPENTRACK_TRACKING_STATUS.TRACKING_COMPLETED,
    );

    const trackingAudit = getEarliest(trackingCompletedEvents);
    return formatSimplfiedRowData(
      trackingAudit?.timestamp,
      trackingAudit,
      null,
      true,
      formatDate(getLatest(trackingCompletedEvents)?.timestamp),
    );
  },
};

/* ----------------------------------- List of simplified events------------------------------------*/

const eventTimeline = [
  {
    sequence: 10,
    label: 'CREATE_DATE',
    type_of_job: [
      'import',
      'export',
      'reposition',
      'shorthaul',
      'intermodal',
      'reload',
    ],
    valueGetter: eventValueGetters.getCreatedDate,
    show: true,
  },
  {
    sequence: 15,
    label: 'TRACKING_INITIATED',
    type_of_job: ['import', 'export', 'reposition', 'intermodal', 'reload'],
    valueGetter: eventValueGetters.getTrackingInitiatedDate,
    show: true,
  },
  {
    sequence: 20,
    label: 'EARLIEST_DATE_TO_GET_EMPTY',
    type_of_job: ['export'],
    valueGetter: eventValueGetters.getEarliestDateToGetEmpty,
    show: true,
  },
  {
    sequence: 30,
    label: 'EMPTY_PICKUP',
    type_of_job: ['export', 'reposition'],
    valueGetter: eventValueGetters.getEmptyPickup,
    show: true,
  },
  {
    sequence: 40,
    label: 'ESTIMATED_ARRIVAL_DATE',
    type_of_job: ['import'],
    valueGetter: eventValueGetters.getEstimatedArrivalDate,
    show: true,
  },
  {
    sequence: 50,
    label: 'ACTUAL_TIME_OF_ARRIVAL',
    type_of_job: ['import'],
    valueGetter: eventValueGetters.getActualTimeOfArrival,
    show: true,
  },
  {
    sequence: 60,
    label: 'DISCHARGED',
    type_of_job: ['import'],
    valueGetter: eventValueGetters.getDischarged,
    show: true,
  },
  {
    sequence: 65,
    label: 'CUSTOM_LFD',
    type_of_job: ['import'],
    valueGetter: eventValueGetters.getCustomLfd,
    show: true,
  },
  {
    sequence: 70,
    label: 'ON_HOLD',
    type_of_job: ['import'],
    valueGetter: eventValueGetters.getOnHold,
    show: true,
  },
  // Rail Events
  {
    sequence: 70,
    label: LOADED_ON_RAIL,
    type_of_job: ['import'],
    valueGetter: eventValueGetters.getRailLoaded,
    show: true,
  },
  {
    sequence: 71,
    label: DEPARTED_ON_RAIL,
    type_of_job: ['import'],
    valueGetter: eventValueGetters.getRailDeparted,
    show: true,
  },
  {
    sequence: 72,
    label: ARRIVED_AT_RAIL_FACILITY,
    type_of_job: ['import'],
    valueGetter: eventValueGetters.getRailArrivedAtFacility,
    show: true,
  },
  {
    sequence: 73,
    label: UNLOADED_FROM_RAIL,
    type_of_job: ['import'],
    valueGetter: eventValueGetters.getRailUnloaded,
    show: true,
  },
  {
    sequence: 74,
    label: AVAILABLE_AT_RAIL_FACILITY,
    type_of_job: ['import'],
    valueGetter: eventValueGetters.getRailAvailableAtFacility,
    show: true,
  },
  {
    sequence: 75,
    label: FULLY_OUTGATED_FROM_RAIL_FACILITY,
    type_of_job: ['import'],
    valueGetter: eventValueGetters.getRailOutgatedAtFacility,
    show: true,
  },
  {
    sequence: 76,
    label: LAST_FREE_DAY_AT_RAIL_FACILITY,
    type_of_job: ['import', 'reload', 'intermodal', 'export'],
    valueGetter: eventValueGetters.getRailLfdAtFacility,
    show: true,
  },
  {
    sequence: 77,
    label: ETA_TO_FINAL_RAIL_FACILITY,
    type_of_job: ['import', 'reload', 'intermodal', 'export'],
    valueGetter: eventValueGetters.getRailEtaFinalFacility,
    show: true,
  },
  {
    sequence: 80,
    label: 'AVAILABLE',
    count: 1,
    titleize: true,
    type_of_job: ['import'],
    valueGetter: eventValueGetters.getAvailable,
    show: true,
  },
  {
    sequence: 90,
    label: 'OFFSITE_PICKUP_SCHEDULED',
    type_of_job: ['import'],
    valueGetter: eventValueGetters.getOffsitePickupScheduled,
    show: true,
  },
  {
    sequence: 90,
    label: 'PICKUP_APPOINTMENT_CONFIRMED',
    type_of_job: ['import'],
    valueGetter: eventValueGetters.getPickupAppointmentConfirmed,
  },
  {
    sequence: 100,
    label: 'OFFSITE_PICKUP_APPOINTMENT_CONFIRMED',
    type_of_job: ['import'],
    valueGetter: eventValueGetters.getOffistePickupAppointmentConfirmed,
  },
  {
    sequence: 100,
    label: 'OFFSITE_PICKUP',
    type_of_job: ['import'],
    valueGetter: eventValueGetters.getOffsitePickup,
    show: true,
  },
  {
    sequence: 110,
    label: 'FULL_OUTGATED',
    type_of_job: ['import'],
    valueGetter: eventValueGetters.getFullOutgate,
    show: true,
  },
  {
    sequence: 120,
    label: 'PICKUP_SCHEDULED',
    type_of_job: ['import', 'shorthaul', 'reload', 'reposition'],
    valueGetter: eventValueGetters.getPickupScheduled,
    show: true,
  },
  {
    sequence: 130,
    label: 'ARRIVED_AT_PICKUP',
    type_of_job: ['export', 'reposition', 'shorthaul', 'reload'],
    valueGetter: eventValueGetters.getArrivedAtPickup,
    show: true,
  },
  {
    sequence: 150,
    label: 'PICKUP',
    type_of_job: [
      'import',
      'reposition',
      'shorthaul',
      'reload',
      'intermodal',
      'export',
    ],
    valueGetter: eventValueGetters.getPickup,
    show: true,
  },
  {
    sequence: 160,
    label: 'TERMINAL_LAST_FREE_DATE',
    type_of_job: ['import'],
    valueGetter: eventValueGetters.getLFD,
    show: true,
  },
  {
    sequence: 170,
    label: 'EARLIEST_RETURN_DATE',
    type_of_job: ['export'],
    valueGetter: eventValueGetters.getEarliestReturnDate,
    show: true,
  },
  {
    sequence: 180,
    label: 'OFFSITE_DELIVER_SCHEDULED',
    type_of_job: ['import'],
    valueGetter: eventValueGetters.getOffsiteDeliveryScheduled,
    show: true,
  },
  {
    sequence: 190,
    label: 'OFFSITE_DELIVERY',
    type_of_job: ['import'],
    valueGetter: eventValueGetters.getOffsiteDelivery,
    show: true,
  },
  {
    sequence: 195,
    label: 'YARD_LAST_FREE_DATE',
    type_of_job: ['import'],
    valueGetter: eventValueGetters.getYardLFD,
    show: true,
  },
  {
    sequence: 200,
    label: 'DELIVERY_SCHEDULED',
    type_of_job: ['import', 'shorthaul', 'reload', 'export', 'reposition'],
    valueGetter: eventValueGetters.getDeliveryScheduled,
    show: true,
  },
  {
    sequence: 200,
    label: 'DELIVERY_APPOINTMENT_CONFIRMED',
    type_of_job: ['import', 'shorthaul', 'reload', 'export', 'reposition'],
    valueGetter: eventValueGetters.getDeliveryAppointmentConfirmed,
    show: true,
  },
  {
    sequence: 200,
    label: 'OFFSITE_DELIVERY_APPOINTMENT_CONFIRMED',
    type_of_job: ['import', 'shorthaul', 'reload', 'export', 'reposition'],
    valueGetter: eventValueGetters.getOffsiteDeliveryAppointmentConfirmed,
    show: true,
  },
  {
    sequence: 210,
    label: 'ARRIVED_AT_DELIVERY',
    type_of_job: ['import', 'shorthaul', 'reload', 'export', 'reposition'],
    valueGetter: eventValueGetters.getArrivedAtDelivery,
    show: true,
  },
  {
    sequence: 220,
    label: 'DELIVERY',
    type_of_job: [
      'import',
      'export',
      'reposition',
      'shorthaul',
      'reload',
      'intermodal',
    ],
    valueGetter: eventValueGetters.getDelivery,
    show: true,
  },
  // {
  //   sequence: 190,
  //   label: "Last Cut Date",
  //   type_of_job: ['export'],
  //
  //
  //
  //   valueGetter: eventValueGetters.getLastCutDate,
  //   show: true,
  // },
  {
    sequence: 240,
    label: 'POD_UPLOAD_DATE',
    type_of_job: ['import', 'export', 'reposition', 'shorthaul', 'reload'],
    valueGetter: eventValueGetters.getPODUploadDate,
    show: true,
  },
  {
    sequence: 250,
    label: 'EMPTY_READY_FOR_PICKUP',
    type_of_job: ['import'],
    valueGetter: eventValueGetters.getEmptyReadyToPickup,
    show: true,
  },
  {
    sequence: 220,
    label: 'EMPTY_RETURN_REPORTED',
    type_of_job: ['import'],
    valueGetter: eventValueGetters.getEmptyReturnReported,
    show: true,
  },
  {
    sequence: 230,
    label: FULL_INGATED_AT_ORIGIN_RAIL_FACILITY,
    type_of_job: ['export'],
    valueGetter: eventValueGetters.getRailIngatedAtOriginFacility,
    show: true,
  },
  {
    sequence: 231,
    label: LOADED_AT_RAIL_ORIGIN,
    type_of_job: ['export'],
    valueGetter: eventValueGetters.getRailLoadedAtOrigin,
    show: true,
  },
  {
    sequence: 232,
    label: DEPARTED_FROM_ORIGIN_RAIL,
    type_of_job: ['export'],
    valueGetter: eventValueGetters.getRailDepartedFromOrigin,
    show: true,
  },
  {
    sequence: 233,
    label: ARRIVED_AT_DESTINATION_RAIL_FACILITY,
    type_of_job: ['export'],
    valueGetter: eventValueGetters.getRailArrivedAtDestinationPortFacility,
    show: true,
  },
  {
    sequence: 234,
    label: UNLOADED_AT_DESTINATION_PORT,
    type_of_job: ['export'],
    valueGetter: eventValueGetters.getRailUnloadedAtDestinationPort,
    show: true,
  },
  {
    sequence: 270,
    label: 'EMPTY_RETURN',
    type_of_job: ['import'],
    valueGetter: eventValueGetters.getEmptyReturn,
    show: true,
  },
  {
    sequence: 280,
    label: 'INVOICE_CREATED',
    type_of_job: ['import', 'shorthaul', 'reload', 'export', 'reposition'],
    valueGetter: eventValueGetters.getInvoiceCreated,
    show: true,
  },
  {
    sequence: 280,
    label: 'CREDIT_MEMO_CREATED',
    type_of_job: ['import', 'shorthaul', 'reload', 'export', 'reposition'],
    valueGetter: eventValueGetters.getCreditMemoCreated,
    show: true,
  },
  {
    sequence: 290,
    label: 'PAYOUT_CREATED',
    type_of_job: ['import', 'shorthaul', 'reload', 'export', 'reposition'],
    valueGetter: eventValueGetters.getPayoutCreated,
    show: true,
  },
  {
    sequence: 300,
    label: 'TRACKING_FAILED',
    type_of_job: ['import', 'export', 'reposition', 'intermodal', 'reload'],
    valueGetter: eventValueGetters.getTrackingFailedDate,
    show: true,
  },
  {
    sequence: 310,
    label: 'TRACKING_COMPLETED',
    type_of_job: ['import', 'export', 'reposition', 'intermodal', 'reload'],
    valueGetter: eventValueGetters.getTrackingCompletedDate,
    show: true,
  },
];

/* ----------------------------------Additional Helpers--------------------------------*/

const getDeliveryStopValue = (stop) => {
  return (
    stop.action === 'deliver' &&
    stop.off_site === false &&
    stop.canceled === false
  );
};

const getDeliveryStopAppointmentConfirmedValue = (stop) => {
  return (
    stop.action === 'deliver' &&
    stop.off_site === false &&
    stop.canceled === false &&
    stop.appointment_unconfirmed === false &&
    stop.empty === false
  );
};

const getPickupStopValue = (stop) => {
  return (
    stop.action === 'pickup' &&
    stop.off_site === true &&
    stop.canceled === false
  );
};

const getEmailOfShipmentCreator = (audits = []) => {
  let createdEvents = (audits ?? []).filter(
    (audit) => audit.event === 'created',
  );
  if (!createdEvents || !createdEvents.length) {
    return null;
  }
  return getLatest(createdEvents).user && getLatest(createdEvents).user.email
    ? getLatest(createdEvents).user.email
    : null;
};

const getEarliest = (events = []) => {
  const eventsArray = events ?? [];
  if (!eventsArray.length) {
    return undefined;
  }
  let earliest = eventsArray[0];
  eventsArray.forEach((event) => {
    if (event.timestamp < earliest.timestamp) {
      earliest = event;
    }
  });
  return earliest;
};

const getLatest = (events = []) => {
  const eventsArray = events ?? [];
  if (!eventsArray.length) {
    return undefined;
  }
  let latest = eventsArray[0];
  eventsArray.forEach((event) => {
    if (event.timestamp > latest.timestamp) {
      latest = event;
    }
  });
  return latest;
};

const formatDate = (timestamp) => {
  if (!timestamp) {
    return null;
  }
  let formattedDate = moment(new Date(timestamp)).format('L');
  if (moment(formattedDate, 'L', true).isValid()) {
    return formattedDate;
  }
  return null;
};

const translatedLabel = (label) => {
  if (
    label &&
    (label.includes('reference numbers changed') ||
      label.includes('reference numbers added'))
  ) {
    const splitted = label.split(' ');
    const eventType = splitted.slice(0, -3).join(' '); // events e.g master bol, VRID, etc
    const slug = splitted.slice(-3).join(' '); // reference numbers changed, reference numbers added
    return (
      <Translation>
        {(t) => t(sluggify(slug + '_EVENT'), { event: t(sluggify(eventType)) })}
      </Translation>
    );
  }
  return <Translation>{(t) => t(sluggify(label))}</Translation>;
};

const isEventRelatedToReferenceNumber = (event) => {
  // Return true if event contains reference_number keywork
  if (event.includes('reference_number')) {
    return true;
  }
  const referenceNumbers = ReferenceNumberTypes.map((item) => item?.name);

  // Define the keywords that indicate an event is related to a reference number
  const relatedKeywords = [
    '_changed',
    '_reference_numbers_added',
    'reference_numbers_changed',
  ];

  // Construct regex pattern to match any of the related keywords
  const regexPattern = new RegExp(relatedKeywords.join('|'), 'gi');

  // Remove related keywords from the event string
  const eventWithoutKeywords = event.replace(regexPattern, '').trim();

  // Check if the modified event matches any of the reference numbers
  return referenceNumbers.includes(eventWithoutKeywords);
};

const formatAuditLabelForAllView = (audit) => {
  const { event } = audit;
  let formattedLabel;
  const meta = { ...audit.meta };
  const stopWindow =
    audit?.event === 'stop_window_change' ||
    audit?.event === 'stop_window_confirmed';

  const needsExtendedLabel =
    ['picked_up_shipment', 'delivered'].includes(event) || stopWindow;
  const extendedLabel = [
    meta?.stopEmpty && 'empty',
    meta?.stopOffsite && 'offsite',
    event,
  ]
    .filter((c) => c) // drop null / false
    .join('_');

  if (meta && needsExtendedLabel) {
    formattedLabel = humanize(extendedLabel);
  } else {
    formattedLabel = renameDocumentUploadedEvent(event, meta?.value);
  }

  if (TERMINAL_HOLD_EVENTS.includes(event)) {
    return <GetTerminalHoldLabel audit={audit} />;
  }

  if (STOP_EVENTS.includes(event)) {
    const offsiteSlug = meta?.stopOffsite && 'OFFSITE';
    const isLoadedOrEmptySlug = meta?.stopEmpty ? 'EMPTY' : 'LOADED';
    const stopActionSlug = meta?.stopType === 'deliver' ? 'DELIVERY' : 'PICKUP';
    const stopTypeSlug = [offsiteSlug, isLoadedOrEmptySlug, stopActionSlug]
      .filter((el) => !!el)
      .join('_');
    const stopSequence = meta?.stopSequence || '';
    const stopAddress = meta?.stopAddress || '';
    const extendedLabel = (
      <Translation>
        {(t) =>
          `${t('SEQUENCE')}: ${stopSequence}, ${t('STOP_TYPE')}: ${t(stopTypeSlug)}, ${t('ADDRESS')}: ${stopAddress}`
        }
      </Translation>
    );

    return (
      <div>
        <span>{translatedLabel(formattedLabel)}</span>
        <span className="pt-2 text-xs break-all text-grey-dark block w-full">
          {extendedLabel}
        </span>
      </div>
    );
  }

  if (BRIDGE_CODE_EVENTS.includes(event)) {
    return <GetBridgeCodeLabel audit={audit} />;
  }

  if (meta && meta.value && typeof meta.value === 'string') {
    const isNum = !Number.isNaN(Number(meta.value));
    /**
     * Format Money When event is not a reference number
     */
    let formatMoneyRequired = isEventRelatedToReferenceNumber(event);
    if (isNum && !formatMoneyRequired) {
      meta.value = formatMoney(Number(meta.value));
    }
    const subStringVal = meta.value.substring(0, 10);
    if (subStringVal.length > 9 && moment(subStringVal).isValid()) {
      const formattedDate = moment(new Date(meta.value)).format('L LT');
      if (moment(formattedDate, 'L LT', true).isValid()) {
        meta.value = formattedDate;
      }
    }
    const changeItem = getChangeItem(meta);
    const laneRateIdLink = getLaneRateIdLink(meta);

    return (
      <div>
        <span>{translatedLabel(formattedLabel)}</span>
        <span
          className="w-48 pt-2 text-xs break-all text-grey-dark block w-full"
          title={meta.value}
        >
          {changeItem}
          {meta?.laneRateId && laneRateIdLink}
        </span>
      </div>
    );
  }

  return formattedLabel;
};

const GetBridgeCodeLabel = ({ audit }) => {
  const { t } = useTranslation();
  const { meta, event } = audit;
  const { exceptions = [] } = meta;
  return (
    <div>
      <span data-testid="span-8a1b7c89">{t(event)}</span>
      <span
        data-testid="span-8a1b7c87"
        className="block pt-2 text-xs break-words text-grey-dark"
      >{`${t('CHANGES')} :`}</span>
      <div
        data-testid="div-8a1b7c87"
        className="pt-1 text-xs break-words text-grey-dark space-y-1"
      >
        {exceptions.map(
          (
            { shipment_leg, bridge_code, reason_code, reason_description },
            idx,
          ) => {
            const baseMessage = `${t('SHIPMENT_LEG')}: ${t(shipment_leg?.toUpperCase())}, ${t('BRIDGE')}: ${bridge_code}, ${t('BRIDGE_REASON_CODE')}: ${reason_code}`;
            const fullMessage = reason_description
              ? `${baseMessage}, ${t('NOTES')}: ${reason_description}`
              : baseMessage;
            return <div key={idx}>{fullMessage}</div>;
          },
        )}
      </div>
    </div>
  );
};

const getLaneRateIdLink = (meta) => {
  if (!meta.laneRateId) return null;
  return (
    <a
      href={`/lane-rates/${meta.laneRateId}/edit`}
      target="_blank"
      rel="noreferrer"
    >
      Lane Rate ID: {meta.laneRateId}
    </a>
  );
};

const GetTerminalHoldLabel = ({ audit }) => {
  const { t } = useTranslation();
  const {
    meta: { status = [] },
    event,
  } = audit;
  const changedStatus = status.map((item) => t(sluggify(item))).join(', ');
  return (
    <div>
      <span>{t(event)}</span>
      <span className="block pt-2 text-xs break-words text-grey-dark">{`${t('CHANGES')}: ${changedStatus}`}</span>
    </div>
  );
};

const getChangeItem = (meta) => {
  const prefix =
    meta.prefixSlug ||
    (!meta._id && 'DISPLAY_CHANGED_ITEM') ||
    'DISPLAY_REMOVED_ITEM';
  const content = { value: meta.value };
  if (meta.prefixSlug) content.text = meta.value;
  return (
    <Translation>
      {(t) =>
        t(prefix, content)
          .split('\n')
          .map((item, i) => <p key={i}>{item}</p>)
      }
    </Translation>
  );
};

const formatAuditDateForAllView = (
  audit,
  formatDateTime,
  dateType = 'date',
) => {
  const stopWindow =
    audit?.event.includes('stop_window_change') ||
    audit?.event.includes('stop_window_confirmed');
  const { timestamp, meta } = audit;
  const isValidDate = meta?.value
    ? moment(meta.value, 'YYYY-MM-DDTHH:mm:ss.SSSZ', true).isValid()
    : false;
  if (!timestamp && !meta?.date) {
    return null;
  }
  let auditDate;
  if (dateType === 'reported_date') {
    auditDate = meta?.date ? meta.date : timestamp;
  } else if (meta && stopWindow && 'windowStart' in meta) {
    auditDate = meta.windowStart;
  } else if (meta && stopWindow && 'windowEnd' in meta) {
    auditDate = meta.windowEnd;
  } else if (isValidDate) {
    auditDate = meta.value;
  } else {
    auditDate = timestamp;
  }
  const formattedDate = formatDateTime(auditDate, DEFAULT_TIMEZONE);
  if (formattedDate !== '-') {
    if (meta && meta?.event_description) {
      return (
        <div>
          <span>{formattedDate}</span>
          <span
            className="w-48 pt-2 text-xs break-all text-grey-dark block w-full"
            title={audit.meta.event_description}
          >
            {humanize(audit.meta.event_description)}
          </span>
        </div>
      );
    }
    return formattedDate;
  }
  return null;
};

const searchAuditsForEvent = (event, audits = [], stopId = 'default') => {
  const filteredAudits = (audits ?? []).filter((audit) => {
    return audit.event === event;
  });
  if (!(filteredAudits && filteredAudits.length)) {
    return null;
  }
  return getLatest(orderBy(filteredAudits, ['timestamp'], ['desc']));
};

const formatSimplfiedRowData = (
  date = null,
  eventAudit = null,
  emailOfShipmentCreator = null,
  isDrayage = true,
  update = null,
) => {
  let source = findEventSource(
    date,
    eventAudit,
    emailOfShipmentCreator,
    isDrayage,
  );
  return {
    date: date ? formatDate(date) : null,
    reported_date: eventAudit?.meta?.date
      ? formatDate(eventAudit.meta.date)
      : formatDate(date),
    updated: findEventUpdate(date, update, eventAudit),
    source,
    channel: source ? eventAudit?.user?.request_source || '' : '',
  };
};

const rowInformation = (stops = [], audits = [], searchValue) => {
  const stopsArray = stops ?? [];
  const auditsArray = audits ?? [];
  const stopId = getStopId(stopsArray);
  const eventAudit = searchAuditsForEvent(searchValue, auditsArray, stopId);
  const userEmail =
    eventAudit?.user?.email || getEmailOfShipmentCreator(auditsArray);
  const date =
    stopsArray && stopsArray.length ? stopsArray[0].window_start : null;
  return { stopId, userEmail, eventAudit, date };
};

const findEventUpdate = (date = null, update = null, eventAudit = null) => {
  if (!date || (!eventAudit && !update)) return null;
  if (update) {
    return formatDate(update);
  }

  if (eventAudit) {
    return formatDate(eventAudit.timestamp);
  }
};

const findEventSource = (
  date,
  eventAudit,
  emailOfShipmentCreator,
  isDrayage,
) => {
  if (!date) return null;
  let source;
  if (eventAudit && eventAudit.user && eventAudit.user.email) {
    source = eventAudit.user.email;
  } else if (!isDrayage && emailOfShipmentCreator) {
    source = emailOfShipmentCreator;
  } else {
    source = null;
  }
  return source;
};

const getStopId = (stops = []) => {
  const stopsArray = stops ?? [];
  return stopsArray?.[0]?._id ?? null;
};

const searchCompletedStopForActualDateOfEvent = (stops = [], timelineEvent) => {
  if (!stops || !stops.length) {
    return null;
  }
  let stop = stops[stops.length - 1];
  let dateInfo = stop.timeline[timelineEvent]
    ? stop.timeline[timelineEvent]
    : null;
  if (dateInfo) {
    if (dateInfo.meta) {
      return dateInfo.meta.date ? dateInfo.meta.date : dateInfo.at;
    }
    return dateInfo.at;
  }
  return null;
};

const findLatestStopAndTimestamp = (stops = [], timelineEvent) => {
  // When we're given several empty pickups. Not implemented yet.
  const stopsArray = stops ?? [];
  if (!stopsArray.length) {
    return [undefined, null];
  }
  let latestStop = stopsArray[0];
  let latestTimestamp = null;
  stopsArray.forEach((stop) => {
    let stopDate;
    let dateInfo = stop.timeline[timelineEvent]
      ? stop.timeline[timelineEvent]
      : null;
    if (dateInfo) {
      if (dateInfo.meta) {
        stopDate = dateInfo.meta.date ? dateInfo.meta.date : dateInfo.at;
      } else {
        stopDate = dateInfo.at;
      }
    } else {
      stopDate = null;
    }
    if (stopDate) {
      if (!latestTimestamp) {
        latestTimestamp = stopDate;
        latestStop = stop;
      } else if (stopDate > latestTimestamp) {
        latestTimestamp = stopDate;
        latestStop = stop;
      }
    }
  });
  return [latestStop, latestTimestamp];
};

const renameDocumentUploadedEvent = (event, value) => {
  if (
    [
      'proof_of_pickup_uploaded',
      'proof_of_delivery_uploaded',
      'proof_of_completion_uploaded',
    ].includes(event) &&
    value
  )
    return humanize('document_uploaded');
  return humanize(event);
};

export {
  populateAndFormatSimplifiedEvents,
  formatAuditEvents,
  eventValueGetters,
  getEarliest,
  getLatest,
  formatDate,
  formatAuditLabelForAllView,
  formatAuditDateForAllView,
  getEmailOfShipmentCreator,
  searchCompletedStopForActualDateOfEvent,
  findEventSource,
  getStopId,
  findLatestStopAndTimestamp,
  searchAuditsForEvent,
  rowInformation,
};
